import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ScrollArea } from "@/components/ui/scroll-area";
import PuffLoader from "react-spinners/PuffLoader";
import ChatMessage from "./ChatMessage";
import { Timer } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../ThemeProvider";

const ChatMessages = ({
  messages,
  isLoading,
  userId,
  userAvatar,
  userName,
  otherUserAvatar,
  otherUserName,
  onMediaClick,
  scrollAreaRef,
  isSellerCountLoading,
  messagesLimitCheck
}) => {
  const [showLoader, setShowLoader] = useState(isLoading);
  const navigate = useNavigate();
  const { theme } = useTheme();

  useEffect(() => {
    let timeout;
    if (isLoading) {
      setShowLoader(true);
    } else {
      timeout = setTimeout(() => setShowLoader(false), 1000);
    }
    return () => clearTimeout(timeout);
  }, [isLoading]);

  useEffect(() => {
    if (!showLoader && scrollAreaRef?.current) {
      const viewport = scrollAreaRef.current.querySelector(
        "[data-radix-scroll-area-viewport]"
      );
      if (viewport) {
        const timeout = setTimeout(() => {
          viewport.scrollTo({
            top: viewport.scrollHeight,
            behavior: "smooth"
          });
        }, 100);
        return () => clearTimeout(timeout);
      }
    }
  }, [messages, showLoader, scrollAreaRef]);

  return (
    <ScrollArea className="flex-grow p-4" ref={scrollAreaRef}>
      <div className="space-y-4">
        {showLoader || isSellerCountLoading ? (
          <motion.div
            className="flex items-center gap-2 justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            Loading Messages
            <PuffLoader
              color={theme === "dark" ? "#ffffff" : "#1d4ed8"}
              size={40}
              margin={4}
            />
          </motion.div>
        ) : (
          <>
            <>
              {messages?.map((message) => (
                <ChatMessage
                  key={message?.id}
                  message={message}
                  isCurrentUser={message.senderId === userId}
                  userAvatar={userAvatar}
                  userName={userName}
                  otherUserAvatar={otherUserAvatar}
                  otherUserName={otherUserName}
                  onMediaClick={onMediaClick}
                />
              ))}
            </>
            {messagesLimitCheck && (
              <motion.div
                className="flex flex-col items-center justify-center p-8 rounded-xl border border-destructive/30 bg-destructive/10 dark:bg-destructive/20 text-center shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                <div className="flex items-center gap-2 text-destructive font-semibold text-lg">
                  <span className="text-xl">🚫</span>
                  Monthly Limit Reached
                </div>

                <p className="mt-3 text-sm text-muted-foreground max-w-md leading-relaxed">
                  You’ve reached your{" "}
                  <span className="font-medium">
                    monthly free messages limit
                  </span>
                  . To continue chatting without interruptions, please upgrade
                  your plan.
                </p>

                <button
                  className="mt-5 inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-primary text-primary-foreground font-medium shadow-md hover:bg-primary/90 transition"
                  onClick={() => navigate("/list-inventory")}
                >
                  <Timer className="w-5 h-5" />
                  Upgrade Plan
                </button>
              </motion.div>
            )}
          </>
        )}
      </div>
    </ScrollArea>
  );
};

export default ChatMessages;
